import {
    ChartCreateOptions,
    FuelVolumeUnits,
    FuelWeightUnits,
    HoverItem,
    ProfileFlownPointsColors,
    ProfileProps,
    RouteAltitudeSource,
    RouteProjectionMode
} from './types';

import {
    assert,
    Color,
    FlightFlownRouteModel,
    FlightForwardRouteModel,
    FlightPlanRouteModel,
    FuelUnit,
    logger,
    PlannedRouteType,
    RouteFlownPointModel,
    RouteWaypointModel,
    RouteWindsData
} from '@fe/core';
import airplaneIcon from '../../img/airplaneIcon.png';
import spriteBarbsMap from '../../img/sprite-barbs.json';
import spriteBarbsImage from '../../img/sprite-barbs.png';
import { MessageType } from '../actor/actor-messages';
import { getGlobalDispatcher } from '../dispatcher';
import geo from '../geodesy/geodesy-helper';
import { Position } from '../geodesy/geodesy-types';
import { distanceAxis } from './chart/axis';
import { Chart } from './chart/chart';
import {
    ChartFeature,
    ChartPoint,
    ChartPointGeometry,
    ChartShadeGeometry,
    ChartVectorDataSourceSpecification,
    customAxisLabels,
    TextAxisLabels
} from './chart/chart-props';
import { getShading } from './convert/day-night';
import { etopsToFeatures } from './convert/etops-to-dataset';
import { impactToDataSet as impactToFeatures } from './convert/impact-to-dataset';
import { decideRouteAltitudeSource, plannedToDataSet as plannedToFeatures } from './convert/planned-to-dataset';
import { getProjection, RouteProjection } from './convert/route-projection';
import { convertFuel, convertUnit } from './convert/unit-converter';
import { ChartPosition, LineStyle } from './util';

export type ProfileHoverHandler = (e: { items: HoverItem[]; event: MouseEvent }) => void;
// ugh...
let _airplaneIcon: HTMLImageElement | undefined;
loadImage(airplaneIcon).then((x) => (_airplaneIcon = x));
let _barbsSprite: CanvasImageSource | undefined;
let _barbsWhite: CanvasImageSource | undefined;
const MS_TO_KT = 1.9438444924406;

loadImage(spriteBarbsImage).then((x) => {
    _barbsSprite = x;
    // Create white version
    const canvas = new OffscreenCanvas(x.width, x.height);
    canvas.width = x.width;
    canvas.height = x.height;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, x.width, x.height);
    ctx.drawImage(x, 0, 0);

    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = 'rgb(255,255,255)';
    ctx.fillRect(0, 0, x.width, x.height);
    _barbsWhite = canvas;
});

const DEFAULT_PROFILE_PROPS: ProfileProps = {
    chart: {
        isDark: false,
        background: {
            color: '#deebf7'
        },
        margin: {
            top: 10,
            right: 10,
            bottom: 33,
            left: 10
        },
        text: {
            color: '#000',
            fontName: 'Arial',
            baseSize: 10
        }
    },
    horizontalProjection: RouteProjectionMode.distanceToOrigin,
    plannedRouteType: PlannedRouteType.atc,
    fuel: {
        formatter: null!,
        weightUnit: FuelWeightUnits.pound,
        volumeUnit: FuelVolumeUnits.gallon
    },
    dayNight: {
        color: '#484848cc',
        line: false,
        shade: false
    },
    drawTerrain: true,
    drawEtops: true,
    drawImpacts: true,
    routeFlown: {
        drawPoints: true,
        drawProjected: true,
        colors: {
            defaultColor: '#f0f',
            projectedColor: '',
            acarsColor: '',
            adsbTerrestrialColor: '',
            adsbSatelliteColor: '',
            faaAnspColor: ''
        }
    },
    barbs: {
        visible: false,
        size: [24, 24],
        spacing: [8, 8]
    },
    heatmap: {
        type: null,
        opacity: 0.25,
        interpolate: true
    }
};

export class Profile {
    private readonly dispatcher = getGlobalDispatcher();
    private _profileProps: ProfileProps = { ...DEFAULT_PROFILE_PROPS };
    private state: {
        flightId: number;
        acid: string;
        departureTime: number | null;
        arrivalTime: number | null;
        weather: {
            winds: RouteWindsData | null;
        };
        commonFuelUnit: FuelUnit;
        routes: {
            projection: RouteProjection | null;
            planned: {
                route: FlightPlanRouteModel | null;
                altitudeSource: RouteAltitudeSource;
            };
            flown: FlightFlownRouteModel | null;
            forward: {
                route: FlightForwardRouteModel | null;
                selectedPoint: number;
            };
        };
    } | null = null;

    private _chart: Chart;
    private _hoverHandler: ProfileHoverHandler | null = null;
    private _goToWaypointHandler: ((waypoint: RouteWaypointModel) => void) | null = null;
    //private hovered: string | null = null;

    constructor(options: ChartCreateOptions) {
        this._chart = new Chart(options.containerId, this._profileProps.chart);
        this._chart.on('mousemove', (e) => {
            const features = this._chart.queryFeatures({ point: { x: e.data.offsetX, y: e.data.offsetY }, layerIds: ['impactsRect'] });
            this._hoverHandler?.({
                items: features
                    .map((x) => x.feature.properties)
                    .filter((x) => !!x)
                    .map((x) => ({ type: 'impact', impact: x })),
                event: e.data
            });
        });
        this._chart.on('click', (e) => {
            const features = this._chart.queryFeatures({
                point: { x: e.data.offsetX, y: e.data.offsetY },
                layerIds: ['flightPlanWaypoints']
            });

            if (features.length > 0) {
                this._goToWaypointHandler?.(features[0].feature.properties as RouteWaypointModel);
            }
        });
        /*
        let hoveredImpact: string | null = null;
        this._chart.on('mousemove', (e) => {
            const features = this._chart.queryFeatures({ point: { x: e.data.offsetX, y: e.data.offsetY }, layerIds: ['flightPlanLines'] });
            if (features.length === 0) {
                if (this.hovered) {
                    this._chart.removeFeatureState('planned', this.hovered, 'hover');
                    this.hovered = null;
                    this._chart.canvas.style.cursor = 'unset';
                }
            } else {
                const firstId = features[0].feature.id;
                if (firstId) {
                    if (this.hovered !== firstId) {
                        this._chart.canvas.style.cursor = 'pointer';
                        if (this.hovered) {
                            this._chart.removeFeatureState('planned', this.hovered, 'hover');
                        }
                        this._chart.setFeatureState('planned', firstId, { hover: true });
                        this.hovered = firstId;
                    }
                }
            }

            const ff = this._chart.queryFeatures({ point: { x: e.data.offsetX, y: e.data.offsetY }, layerIds: ['impactsRect'] });
            if (ff.length === 0) {
                if (hoveredImpact) {
                    this._chart.removeFeatureState('impact', hoveredImpact, 'hover');
                }
                hoveredImpact = null;
            } else {
                const imp = ff[0]?.feature.id;
                if (imp) {
                    if (imp !== hoveredImpact) {
                        if (hoveredImpact) {
                            this._chart.removeFeatureState('impact', hoveredImpact, 'hover');
                        }
                        hoveredImpact = imp;
                        this._chart.setFeatureState('impact', imp, { hover: true });
                    }
                } else {
                    if (hoveredImpact) {
                        this._chart.removeFeatureState('impact', hoveredImpact, 'hover');
                        hoveredImpact = null;
                    }
                }
            }
        });*/
        this.updateScalesAndLayers();
    }

    setHoverHandler(handler: ProfileHoverHandler) {
        this._hoverHandler = handler;
    }

    setGoToWaypointHandler(handler: typeof this._goToWaypointHandler) {
        this._goToWaypointHandler = handler;
    }

    setProps(props: Partial<ProfileProps>): void {
        const projectionChanged =
            props.horizontalProjection != null && this._profileProps.horizontalProjection !== props.horizontalProjection;
        const drawProjectedChanged =
            props.routeFlown?.drawProjected !== undefined && props.routeFlown.drawProjected !== this._profileProps.routeFlown.drawProjected;
        const terrainChanged = props.drawTerrain !== undefined && this._profileProps.drawTerrain !== props.drawTerrain;
        Object.assign(this._profileProps, props);
        this.updateScalesAndLayers();
        this._chart.setProps(this._profileProps.chart);
        if (projectionChanged && this.state?.routes.planned.route) {
            this.state.routes.projection = getProjection(this._profileProps.horizontalProjection, this.state.routes.planned.route);
            this.updateFlightPlan();
        }

        if (drawProjectedChanged) {
            this.updateRouteFlownData();
        }

        if (terrainChanged) {
            this.updateTerrainData();
        }
    }

    setFlightForwardPointsCurrentIndex(index: number) {
        if (this.state) {
            this.state.routes.forward.selectedPoint = index;
            this._chart.triggerRepaint();
        }
    }

    setFlight(data: {
        flightId: number;
        acid: string;
        departureTime: number | null;
        arrivalTime: number | null;
        weather: {
            winds: RouteWindsData | null;
        };
        routes: {
            planned: FlightPlanRouteModel | null;
            flown: FlightFlownRouteModel | null;
            forward: FlightForwardRouteModel | null;
        };
    }): boolean {
        try {
            const prevState = this.state;
            this.state = {
                flightId: data.flightId,
                acid: data.acid,
                departureTime: data.departureTime,
                arrivalTime: data.arrivalTime,
                weather: {
                    winds: data.weather.winds
                },
                commonFuelUnit:
                    data.routes.planned?.fuelUnit || data.routes.flown?.find((x) => x.fobUnit !== null)?.fobUnit || FuelUnit.Null,
                routes: {
                    projection: data.routes.planned ? getProjection(this._profileProps.horizontalProjection, data.routes.planned) : null,
                    planned: { route: data.routes.planned, altitudeSource: decideRouteAltitudeSource(data.routes.planned) },
                    flown: [...(data.routes.flown ?? [])], // Just in case we got an immutable copy
                    forward: {
                        route: data.routes.forward,
                        selectedPoint: 0
                    }
                }
            };

            // Changing the flight has impact on everything
            if (prevState?.flightId !== data.flightId) {
                this._chart.resetViewState();
                this._chart.removeAllData();
                this.updateFlightPlan();
                logger.info('Profile: flight changed');
            } else {
                // Detect individual changes
                if (prevState?.routes.planned.route !== data.routes.planned) {
                    // Changing flight plan has impact on everything
                    logger.info('Profile: flight plan changed');
                    this._chart.removeAllData();
                    this.updateFlightPlan();
                } else {
                    if (prevState.routes.flown !== data.routes.flown) {
                        logger.info('Profile: Route flown changed');
                        this.updateRouteFlownData();
                    }

                    if (prevState.routes.forward.route !== data.routes.forward) {
                        logger.info('Profile: Route forward changed');
                        this.updateRouteForwardData();
                    }

                    if (prevState.arrivalTime !== data.arrivalTime || prevState.departureTime !== data.departureTime) {
                        this.updateDayNightData();
                    }

                    if (prevState.weather.winds !== data.weather.winds) {
                        logger.info('Profile: Weather winds changed');
                        this.updateWeatherData();
                    }
                }
            }
            return true;
        } catch (err) {
            this.state = null;
            this.dispatcher.dispatch({ type: MessageType.cancelLoadTerrain });
            this._chart.removeAllData();
            logger.trackException(err);
            this._chart.triggerRepaint();
            return false;
        }
    }

    addFlownPoint(p: { index: number; point: RouteFlownPointModel }): void {
        if (!this.state?.routes.projection || !this.state.routes.flown) {
            return;
        }

        this.state.routes.flown.splice(p.index, 0, p.point);
        this.updateRouteFlownData();
    }

    finalize() {
        this.dispatcher.dispatch({ type: MessageType.cancelLoadTerrain });
        this._chart.finalize();
    }

    private updateScalesAndLayers() {
        this._chart.setScales({
            zeroY: {
                position: ChartPosition.left,
                min: 0,
                max: 1
            },
            altitude: {
                position: ChartPosition.left,
                min: 0,
                max: 500,
                sizePixels: 50,
                axisLabels: {
                    type: 'text',
                    label: (x) => (x === 0 ? '0' : `FL${x}`),
                    labelStep: altitudeAxisStepFunc,
                    bigTick: altitudeAxisStepFunc,
                    smallTick: altitudeAxisStepSmallTickFunc,
                    color: this._profileProps.chart.isDark ? '#fff' : '#000'
                }
            },
            fuel: {
                position: ChartPosition.right,
                autoScale: true,
                sizePixels: 60,
                hideWhenNoData: true,
                axisLabels: {
                    type: 'text',
                    label: (x) => this._profileProps.fuel.formatter.formatFuel({ value: x, unit: this.state!.commonFuelUnit }),
                    labelStep: this.fobLabelAxisStepFunc,
                    bigTick: this.fobLabelAxisStepFunc,
                    smallTick: this.fobSmallTickAxisStepFunc,
                    color: this._profileProps.chart.isDark ? '#fff' : '#000'
                }
            },
            distance: {
                position: ChartPosition.top,
                autoScale: true,
                sizePixels: this._profileProps.dayNight.line ? 33 : 22,
                axisLabels: customAxisLabels({
                    type: 'custom',
                    draw: distanceAxis,
                    args: {
                        textColor: this._profileProps.chart.isDark ? '#fff' : '#000',
                        pattern: this._profileProps.chart.isDark ? ['#000', '#fff'] : ['#fff', '#000'],
                        offset: this._profileProps.dayNight.line ? -12 : 0
                    }
                })
            }
        });

        this._chart.setLayers([
            {
                id: 'day-night-line',
                visible: this._profileProps.dayNight.line,
                xAxisId: 'distance',
                yAxisId: 'zeroY',
                sourceId: 'day-night-shading',
                type: 'shade-line',
                paint: {
                    dayColor: '#abe0f6',
                    nightColor: '#434490',
                    outlineColor: this._profileProps.chart.isDark ? '#fff' : '#000'
                }
            },
            {
                visible: !!this._profileProps.heatmap.type,
                id: 'weather-heatmap',
                type: 'raster',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'weather-raster',
                colorRamp: this._profileProps.heatmap.type === 'temperature' ? heatmapTemperature : heatmapWind,
                getValue: this._profileProps.heatmap.type === 'winds' ? (e) => e[0] : (e) => e[2],
                paint: {
                    opacity: this._profileProps.heatmap.opacity,
                    interpolate: this._profileProps.heatmap.interpolate
                }
            },
            {
                id: 'terrain',
                visible: this._profileProps.drawTerrain,
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'terrain',
                type: 'rectangle',
                paint: {
                    fill: '#13c216'
                }
            },
            {
                id: 'day-night-shade',
                visible: this._profileProps.dayNight.shade,
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'day-night-shading',
                type: 'shade',
                paint: {
                    color: this._profileProps.dayNight.color
                }
            },
            {
                visible: this._profileProps.barbs.visible,
                id: 'weather-winds',
                type: 'icon-grid',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'weather-raster',
                sprite: this._profileProps.chart.isDark ? _barbsWhite! : _barbsSprite!,
                iconSize: this._profileProps.barbs.size,
                iconSpacing: this._profileProps.barbs.spacing,
                getIcon: (value) => {
                    const barb = getWindImageName(value[0]);
                    return {
                        x: spriteBarbsMap[barb].x,
                        y: spriteBarbsMap[barb].y,
                        w: spriteBarbsMap[barb].width,
                        h: spriteBarbsMap[barb].height
                    };
                }
            },
            {
                id: 'flightPlan',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'planned',
                filter: (x) => x.geometry.type === 'Line',
                type: 'line',
                paint: {
                    color:
                        this._profileProps.plannedRouteType === PlannedRouteType.planned
                            ? 'rgba(0, 255, 255, 0.5)'
                            : 'rgba(0, 255, 0, 0.5)',
                    width: 2,
                    style: LineStyle.solid
                }
            },
            {
                id: 'flightPlanLines',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'planned',
                type: 'grid-lines',
                layout: {
                    axis: 'x'
                },
                paint: {
                    lineColor: this._profileProps.chart.isDark ? '#444' : '#a5a5a5',
                    lineWidth: 1,
                    lineStyle: LineStyle.dashed,
                    hoverColor: '#f00'
                }
            },
            {
                id: 'flightPlanWaypoints',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'planned',
                type: 'axis-labels',
                layout: {
                    axis: 'x',
                    position: ChartPosition.bottom,
                    allowOverlap: false,
                    getText: (x) => x.properties?.name ?? '',
                    fontSize: 8
                },
                paint: {
                    textColor: (_, state) => (state?.hover ? '#f00' : this._profileProps.chart.isDark ? '#fff' : '#000')
                }
            },
            {
                id: 'altitudeIndicator',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'flown',
                type: 'axis-indicator',
                layout: {
                    axis: 'y',
                    value: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return lastFeature.geometry.coordinates[1];
                    },
                    text: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return lastFeature.geometry.coordinates[1].toFixed(0);
                    }
                },
                paint: {
                    textColor: '#fff',
                    fillColor: '#f0f'
                }
            },
            {
                id: 'fobIndicator',
                xAxisId: 'distance',
                yAxisId: 'fuel',
                sourceId: 'fob',
                type: 'axis-indicator',
                layout: {
                    axis: 'y',
                    value: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return lastFeature.geometry.coordinates[1];
                    },
                    text: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return this._profileProps.fuel.formatter.formatFuel({
                            value: lastFeature.geometry.coordinates[1],
                            unit: this.state!.commonFuelUnit
                        });
                    }
                },
                paint: {
                    textColor: '#fff',
                    fillColor: '#0078fa'
                }
            },
            {
                id: 'distanceIndicator',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'flown',
                type: 'axis-indicator',
                layout: {
                    axis: 'x',
                    offset: this._profileProps.dayNight.line ? -14 : 0,
                    value: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return lastFeature.geometry.coordinates[0];
                    },
                    text: (x) => {
                        const lastFeature = x.at(-1)!;
                        assert(lastFeature.geometry.type === 'Point');
                        return lastFeature.geometry.coordinates[0].toFixed(0);
                    }
                },
                paint: {
                    textColor: '#fff',
                    fillColor: '#f0f'
                }
            },
            {
                id: 'actualFobLine',
                xAxisId: 'distance',
                yAxisId: 'fuel',
                sourceId: 'fob',
                type: 'line',
                paint: {
                    color: '#0078fa',
                    width: 1
                }
            },
            {
                id: 'actualFobPoint',
                xAxisId: 'distance',
                yAxisId: 'fuel',
                sourceId: 'fob',
                type: 'circle',
                paint: {
                    color: '#0078fa',
                    radius: 2,
                    hollow: true
                }
            },
            {
                id: 'plannedFobFobLine',
                xAxisId: 'distance',
                yAxisId: 'fuel',
                sourceId: 'plannedFob',
                type: 'line',
                paint: {
                    color: '#0000ff',
                    width: 1
                }
            },
            {
                id: 'plannedFobFobPoint',
                xAxisId: 'distance',
                yAxisId: 'fuel',
                sourceId: 'plannedFob',
                type: 'circle',
                paint: {
                    color: '#0000ff',
                    radius: 2,
                    hollow: true
                }
            },
            {
                visible: this._profileProps.drawEtops,
                id: 'etopsRect',
                xAxisId: 'distance',
                yAxisId: 'zeroY',
                sourceId: 'etops',
                type: 'rectangle',
                paint: {
                    fill: 'rgba(0, 75, 148, 0.4)',
                    outlieWidth: 1,
                    outlineColor: 'rgba(2, 50, 99, 1)'
                }
            },
            /*{
                visible: this._profileProps.drawWeather,
                id: 'weather-rect',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'weather',
                type: 'rectangle',
                paint: {
                    fill: 'rgba(255, 75, 148, 0.4)',
                    outlieWidth: 0,
                    outlineColor: 'rgba(2, 50, 99, 1)',
                    textColor: '#fff'
                }
            },*/
            {
                visible: this._profileProps.drawImpacts,
                id: 'impactsRect',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'impact',
                type: 'rectangle',
                paint: {
                    fill: '#ff007755',
                    outlineColor: '#000',
                    outlieWidth: (_, state) => (state?.hover ? 4 : 0)
                }
            },
            {
                visible: this._profileProps.drawEtops,
                id: 'etopsLabel',
                xAxisId: 'distance',
                yAxisId: 'zeroY',
                sourceId: 'etops',
                type: 'text',
                paint: {
                    color: '#fff',
                    textField: 'tag'
                }
            },
            {
                visible: this._profileProps.drawImpacts,
                id: 'impactsLabel',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'impact',
                type: 'text',
                paint: {
                    color: '#fff',
                    textField: 'name'
                }
            },
            {
                id: 'flownLine',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'flown',
                type: 'line',
                paint: {
                    color: '#ff00ff',
                    width: 2
                }
            },
            {
                id: 'flownPoints',
                visible: this._profileProps.routeFlown.drawPoints,
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'flown',
                type: 'circle',
                paint: {
                    color: (feature) => toFlownColor(this._profileProps.routeFlown.colors, feature.properties!) as Color,
                    radius: 2
                }
            },
            {
                id: 'route-forward',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'forward',
                filter: (_, index) => index <= this.state!.routes.forward.selectedPoint,
                type: 'line',
                paint: {
                    width: 2,
                    color: 'rgb(112, 77, 255)',
                    style: LineStyle.dashed
                }
            },
            {
                id: 'planeIcon',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'flown',
                type: 'icon',
                layout: {
                    getPosition: (x) => x.at(-1)!
                },
                paint: {
                    icon: _airplaneIcon,
                    size: { width: 30, height: 30 },
                    offset: [-15, -15]
                }
            },
            {
                id: 'route-forward-icon',
                xAxisId: 'distance',
                yAxisId: 'altitude',
                sourceId: 'forward',
                filter: (_, index) => index <= this.state!.routes.forward.selectedPoint,
                type: 'icon',
                layout: {
                    getPosition: (d) => d.at(this.state!.routes.forward.selectedPoint) ?? null
                },
                paint: {
                    icon: _airplaneIcon,
                    size: { width: 30, height: 30 },
                    offset: [-15, -15]
                }
            }
        ]);

        this._chart.triggerRepaint();
    }

    private updateFlightPlan() {
        this._chart.removeSource('planned');
        this._chart.removeSource('plannedFob');
        this._chart.removeSource('etops');
        this._chart.removeSource('impact');
        if (!this.state?.routes.planned.route || !this.state.routes.projection) {
            this._chart.removeAllData();
            return;
        }

        const planned = addDistance(this.state.routes.projection, this.state.routes.planned.route.waypoints, (x) => x.position);
        const plannedFeatures = plannedToFeatures(
            planned,
            this.state.routes.planned.altitudeSource,
            this.state.routes.planned.route.cruisingAltitude
        );

        this._chart.upsertSource('planned', plannedFeatures);
        this._chart.upsertSource('plannedFob', {
            type: 'vector',
            features: planned
                .filter((x) => x[1].fob !== null)
                .map(
                    (x) =>
                        ({
                            geometry: {
                                type: 'Point',
                                coordinates: [x[0], this.fuelToCommonUnit(x[1].fob!, this.state!.routes.planned.route!.fuelUnit)]
                            }
                        }) satisfies ChartFeature<ChartPointGeometry>
                )
        });
        this._chart.upsertSource('etops', {
            type: 'vector',
            features: etopsToFeatures(this.state.routes.planned.route.etops, planned)
        });
        this._chart.upsertSource('impact', {
            type: 'vector',
            features: impactToFeatures(this.state.routes.projection, this.state.routes.planned.route.impacts, planned)
        });

        // All of these depend on the flight plan
        this.updateRouteFlownData();
        this.updateRouteForwardData();
        this.updateDayNightData();
        this.updateWeatherData();
        this.updateTerrainData();
    }

    private updateRouteFlownData() {
        this._chart.removeSource('fob');
        this._chart.removeSource('flown');

        if (!this.state?.routes.flown || !this.state.routes.projection) {
            return;
        }

        const routeFlownData = this._profileProps.routeFlown.drawProjected
            ? this.state.routes.flown
            : this.state.routes.flown.filter((x) => x.feedId !== 999);

        const flown = addDistance(this.state.routes.projection, routeFlownData, (x) => x.position);
        const flownFeatures = {
            type: 'vector',
            features: flown
                .filter((x) => x[1].altitude !== null)
                .map(
                    ([distance, point]) =>
                        ({
                            geometry: { type: 'Point', coordinates: [distance, (point.altitude ?? 0) / 100] },
                            properties: point
                        }) satisfies ChartFeature<ChartPointGeometry, RouteFlownPointModel>
                )
        } satisfies ChartVectorDataSourceSpecification;

        this._chart.upsertSource('flown', flownFeatures);
        this._chart.upsertSource('fob', {
            type: 'vector',
            features: flown
                .filter((x) => x[1].fob != null && x[1].fobUnit != null)
                .map(
                    (x) =>
                        ({
                            geometry: {
                                type: 'Point',
                                coordinates: [x[0], this.fuelToCommonUnit(x[1].fob!, x[1].fobUnit!)]
                            },
                            properties: x[1]
                        }) satisfies ChartFeature<ChartPointGeometry>
                )
        });
    }

    private updateRouteForwardData() {
        this._chart.removeSource('forward');

        if (!this.state?.routes.forward || !this.state.routes.projection) {
            return;
        }

        const forwardSegments = addDistance(
            this.state.routes.projection,
            this.state.routes.forward.route?.points ?? [],
            (x) => x.position
        ).filter((x) => x[1].altitude != null);
        this._chart.upsertSource('forward', {
            type: 'vector',
            features: forwardSegments.map(
                ([distance, point]) =>
                    ({
                        geometry: {
                            type: 'Point',
                            coordinates: [distance, (point.altitude ?? 0) / 100]
                        }
                    }) satisfies ChartFeature
            )
        });
    }

    private updateDayNightData() {
        this._chart.removeSource('day-night-shading');
        if (!this.state?.routes.planned.route || !this.state.routes.projection) {
            return;
        }

        const features = this.getDayNightShadingFeatures(this.state.routes.planned.route.waypoints);
        this._chart.upsertSource('day-night-shading', {
            type: 'vector',
            features: features
        });
    }

    private updateWeatherData() {
        this._chart.removeSource('weather');

        const winds = this.state?.weather.winds;
        if (!winds || this.state?.routes.projection == null) {
            return;
        }

        const data = {
            altitudes: winds.altitudes.map((a) => a / 100),
            points: winds.elements.map((pt) => ({
                distance: pt.oar,
                dir: pt.dar,
                altitudes: pt.alts.map((at) => {
                    const value = (at.u * Math.cos((-pt.dar / 180) * Math.PI) - at.v * Math.sin((-pt.dar / 180) * Math.PI)) * MS_TO_KT;
                    return [Math.abs(value), value > 0 ? 0 : 180, at.t];
                })
            }))
        };

        const maxOffset = winds.elements.at(-1)!.oar;

        this._chart.upsertSource('weather-raster', {
            type: 'raster',
            bounds: [0, winds.altitudes.at(-1)! / 100, maxOffset, winds.altitudes[0] / 100],
            getValueBounds: (x, y) => {
                if (x > maxOffset) {
                    return null;
                }

                const distanceIndex = data.points.findLastIndex((p) => x >= p.distance);
                const altitudeIndex = data.altitudes.findLastIndex((at) => y >= at);

                if (distanceIndex === -1 || altitudeIndex === -1) {
                    return null;
                }

                if (altitudeIndex + 1 >= data.altitudes.length || distanceIndex + 1 >= data.points.length) {
                    return { value: data.points[distanceIndex].altitudes[altitudeIndex] };
                }

                const x1y1Value = data.points[distanceIndex].altitudes[altitudeIndex];
                const x1y2Value = data.points[distanceIndex].altitudes[altitudeIndex + 1];

                const x2y1Value = data.points[distanceIndex + 1].altitudes[altitudeIndex];
                const x2y2Value = data.points[distanceIndex + 1].altitudes[altitudeIndex + 1];

                const x1 = data.points[distanceIndex].distance;
                const y1 = data.altitudes[altitudeIndex];

                const x2 = data.points[distanceIndex + 1].distance;
                const y2 = data.altitudes[altitudeIndex + 1];

                return {
                    values: [
                        [x1y1Value, x1y2Value],
                        [x2y1Value, x2y2Value]
                    ],
                    bounds: [
                        [x1, y1],
                        [x2, y2]
                    ]
                };
            },
            getValue: (x, y) => {
                if (x > maxOffset) {
                    return null;
                }

                const xPointIndex = data.points.findLastIndex((p) => x >= p.distance);
                let altitudeIndex = data.altitudes.findLastIndex((at) => y >= at);
                if (xPointIndex === -1 || altitudeIndex === -1) {
                    return null;
                }

                let xPoint = data.points[xPointIndex];
                if (xPointIndex + 1 < data.points.length) {
                    const nextPoint = data.points[xPointIndex + 1];
                    if (nextPoint.distance - x < x - xPoint.distance) {
                        // Snap to next point if closer
                        xPoint = nextPoint;
                    }
                }

                if (altitudeIndex + 1 < data.altitudes.length) {
                    const nextAltitude = data.altitudes[altitudeIndex + 1];

                    if (nextAltitude - y < y - data.altitudes[altitudeIndex]) {
                        // Snap to closer altitude
                        altitudeIndex += 1;
                    }
                }

                const yPoint = xPoint.altitudes[altitudeIndex];
                return yPoint;
            }
        });
    }

    private updateTerrainData() {
        this._chart.removeSource('terrain');
        this.dispatcher.dispatch({ type: MessageType.cancelLoadTerrain });
        const planned = this.state?.routes.planned;
        if (!planned?.route || !this.state?.routes.projection || !this._profileProps.drawTerrain) {
            return;
        }

        this.dispatcher
            .dispatch({
                type: MessageType.loadTerrain,
                data: { id: planned.route.id, waypoints: planned.route.waypoints }
            })
            .then((data) => {
                // Flight changed in the mean time, even though we're cancelling the request in the code above,
                // we can still end up in a state where terrain data already loaded and promise resolve was already queued by the scheduler
                if (planned !== this.state?.routes.planned) {
                    return;
                }
                this._chart.upsertSource('terrain', {
                    type: 'vector',
                    features: [
                        {
                            geometry: {
                                type: 'Polygon',
                                coordinates: [
                                    [this.state!.routes!.projection?.project(data[0].position), 0],
                                    ...data.map((x) => [this.state!.routes.projection!.project(x.position), x.altitude]),
                                    [this.state!.routes!.projection?.project(data.at(-1)!.position), 0]
                                ] as ChartPoint[]
                            }
                        }
                    ]
                });
            })
            .catch((e) => logger.info('Terrain abort', e));
    }

    private fuelToCommonUnit(value: number, sourceFuelUnit: FuelUnit): number {
        return Math.round(convertFuel(value, sourceFuelUnit, this.state!.commonFuelUnit));
    }

    private getDayNightShadingFeatures(route: RouteWaypointModel[]): ChartFeature<ChartShadeGeometry>[] {
        if (!this.state?.departureTime || !this.state.arrivalTime || !this.state.routes.projection) {
            return [];
        }

        const departure = new Date(this.state.departureTime * 1000);
        const arrival = new Date(this.state.arrivalTime * 1000);

        // Shading path: 100 NM steps
        const positions = geo.addPointsAlongRoute(
            route.map((x) => x.position),
            100
        );
        const shadingRoute = positions.map((x) => [this.state!.routes.projection!.project(x), x]) as [number, Position][];
        const shading = getShading(departure, arrival, shadingRoute);

        return [
            {
                geometry: {
                    type: 'Shade',
                    bounds: [
                        [0, 0],
                        [shadingRoute.at(-1)![0], 500]
                    ],
                    stops: shading
                }
            }
        ];
    }

    private fobLabelAxisStepFunc: NonNullable<Exclude<TextAxisLabels['labelStep'], number>> = (_min, _max, availableSpace) => {
        const displayUnit = convertUnit(this.state!.commonFuelUnit, this._profileProps.fuel.weightUnit, this._profileProps.fuel.volumeUnit);
        const min = convertFuel(_min, this.state!.commonFuelUnit, displayUnit);
        const max = convertFuel(_max, this.state!.commonFuelUnit, displayUnit);
        const range = max - min;
        const rawStep = range / (availableSpace / 20);
        const magnitude = 10 ** Math.floor(Math.log10(rawStep));
        const normalized = rawStep / magnitude;
        const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

        const step = niceNormalized * magnitude;

        return convertFuel(step, displayUnit, this.state!.commonFuelUnit);
    };

    private fobSmallTickAxisStepFunc: TextAxisLabels['labelStep'] = (min, max, availableSpace) => {
        return this.fobLabelAxisStepFunc(min, max, availableSpace) / 2;
    };
}

function addDistance<T>(projection: RouteProjection, items: T[], getPosition: (item: T) => Position): [number, T][] {
    const result = [] as [number, T][];

    for (const item of items) {
        result.push([projection.project(getPosition(item)), item]);
    }
    return result;
}

function loadImage(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imageUrl;
        image.onload = () => {
            resolve(image);
            image.onerror = (error) => {
                reject(new Error(`Error loading image: ${error}`));
            };
        };
    });
}

export function toFlownColor(colors: ProfileFlownPointsColors, point: RouteFlownPointModel): string {
    // Unknown, use default color
    if (point.feedId === null) {
        return colors.defaultColor;
    }
    // Projected
    if (point.feedId === 999) {
        return colors.projectedColor;
    }
    // ACARS/3PDI
    if (point.feedId >= 1000 && point.feedId !== 1031) {
        return colors.acarsColor;
    }
    // ADS-B Terrestrial
    if (point.feedId === 190 || point.feedId === 192 || point.feedId === 194 || point.feedId === 196 || point.feedId === 198) {
        return colors.adsbTerrestrialColor;
    }
    // ADS-B Satellite
    if (point.feedId === 191 || point.feedId === 193 || point.feedId === 195 || point.feedId === 197 || point.feedId === 199) {
        return colors.adsbSatelliteColor;
    }
    // FAA/ANSP & all other
    // ASDE-X (200-299), Mexico (108), Canada (101), UK (102), NZ (105 & 106), ASA (1031).
    // Also default everything else to this color
    return colors.faaAnspColor;
}

const altitudeAxisStepFunc: TextAxisLabels['labelStep'] = (min, max, availableSpace) => {
    switch (true) {
        case availableSpace >= 800:
            return 10;
        case availableSpace >= 600:
            return 25;
        case availableSpace >= 400:
            return 50;
        default:
            return 100;
    }
};

const altitudeAxisStepSmallTickFunc: TextAxisLabels['labelStep'] = (min, max, availableSpace) => {
    return altitudeAxisStepFunc(min, max, availableSpace) / 2;
};

function getWindImageName(input: number): keyof typeof spriteBarbsMap {
    // TODO: improve this mapping lol
    /* eslint-disable curly */
    if (input <= 0) return 'wind-0';
    if (input <= 7) return 'wind-7';
    if (input <= 12) return 'wind-12';
    if (input <= 17) return 'wind-17';
    if (input <= 22) return 'wind-22';
    if (input <= 27) return 'wind-27';
    if (input <= 32) return 'wind-32';
    if (input <= 37) return 'wind-37';
    if (input <= 42) return 'wind-42';
    if (input <= 47) return 'wind-47';
    if (input <= 52) return 'wind-52';
    if (input <= 57) return 'wind-57';
    if (input <= 62) return 'wind-62';
    if (input <= 67) return 'wind-67';
    if (input <= 72) return 'wind-72';
    if (input <= 77) return 'wind-77';
    if (input <= 82) return 'wind-82';
    if (input <= 87) return 'wind-87';
    if (input <= 92) return 'wind-92';
    if (input <= 97) return 'wind-97';
    if (input <= 102) return 'wind-102';
    if (input <= 107) return 'wind-107';
    if (input <= 112) return 'wind-112';
    if (input <= 117) return 'wind-117';
    if (input <= 122) return 'wind-122';
    if (input <= 127) return 'wind-127';
    if (input <= 132) return 'wind-132';
    if (input <= 137) return 'wind-137';
    if (input <= 142) return 'wind-142';
    if (input <= 147) return 'wind-147';
    if (input <= 152) return 'wind-152';
    if (input <= 202) return 'wind-202';

    return 'wind-0';
}

const heatmapTemperature = [
    { color: [0xee, 0xee, 0xee, 0xeb], value: -128 },
    { color: [0xee, 0xee, 0xee, 0xeb], value: -40 },
    { color: [0xff, 0xaa, 0xff, 0xeb], value: -30 },
    { color: [0x91, 0x09, 0x91, 0xeb], value: -20 },
    { color: [0x24, 0x18, 0x6a, 0xeb], value: -15 },
    { color: [0x55, 0x4e, 0xb1, 0xeb], value: -10 },
    { color: [0x3e, 0x79, 0xc6, 0xeb], value: -5 },
    { color: [0x4b, 0xb6, 0x98, 0xeb], value: 0 },
    { color: [0x59, 0xd0, 0x49, 0xeb], value: 5 },
    { color: [0xbe, 0xe4, 0x3d, 0xeb], value: 10 },
    { color: [0xeb, 0xd7, 0x35, 0xeb], value: 15 },
    { color: [0xea, 0xa4, 0x3e, 0xeb], value: 20 },
    { color: [0xe5, 0x6d, 0x53, 0xeb], value: 25 },
    { color: [0xbe, 0x30, 0x66, 0xeb], value: 30 },
    { color: [0x6b, 0x15, 0x27, 0xff], value: 40 },
    { color: [0x2b, 0x00, 0x01, 0xff], value: 50 },
    { color: [0x2b, 0x00, 0x01, 0xff], value: 127 }
] satisfies { color: [number, number, number, number]; value: number }[];

const heatmapWind = [
    { color: [0x52, 0x47, 0x8d, 0x6b], value: 0 },
    { color: [0x51, 0x56, 0xb7, 0xd1], value: 5 },
    { color: [0x3c, 0x7f, 0xc6, 0xd9], value: 10 },
    { color: [0x47, 0xad, 0xa3, 0xd9], value: 15 },
    { color: [0x4a, 0xc7, 0x55, 0xd9], value: 20 },
    { color: [0x78, 0xd4, 0x4d, 0xd9], value: 25 },
    { color: [0xc1, 0xe5, 0x3c, 0xd9], value: 30 },
    { color: [0xeb, 0xe0, 0x35, 0xd9], value: 35 },
    { color: [0xea, 0xbd, 0x38, 0xd9], value: 40 },
    { color: [0xe9, 0x95, 0x42, 0xd9], value: 45 },
    { color: [0xe5, 0x6d, 0x53, 0xd9], value: 50 },
    { color: [0xce, 0x3b, 0x6d, 0xd9], value: 60 },
    { color: [0xad, 0x2a, 0x59, 0xde], value: 70 },
    { color: [0x65, 0x14, 0x19, 0xed], value: 90 },
    { color: [0x2b, 0x00, 0x01, 0xff], value: 110 },
    { color: [0x2b, 0x00, 0x01, 0xff], value: 255 }
] satisfies { color: [number, number, number, number]; value: number }[];
